/**
 * Login Component
 * Coláiste Lurgan Management System - User authentication form
 */

import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LanguageSwitcher } from '../ui/language-switcher';
import { LoadingSpinner } from '../ui/loading';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';

import { ReactComponent as LurganLogo } from '../../assets/logos-svg/Lurgan-Horizontal-Vector_Lurgan-Logo-Dubh.svg';
import { ReactComponent as BainisteoirLogo } from '../../assets/logos-svg/Bainisteoir-Lurgan-Vector.svg';

// Redirect PARENT users with no name to /profile on first login so they can fill in their details.
function getPostLoginDestination(user, defaultDestination) {
  if (user?.user_type === 'PARENT' && !user?.first_name) {
    return '/profile';
  }
  return defaultDestination;
}

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoggingIn, loginError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // MFA state
  const [mfaSession, setMfaSession] = useState(null);
  const [cachedCredentials, setCachedCredentials] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  // Validation schema
  const loginSchema = z.object({
    username: z.string().min(1, t('login.errors.username')),
    password: z.string().min(1, t('login.errors.password')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  // Step 1 — credentials submit
  const onSubmit = async (data) => {
    if (isLoggingIn) return;

    const loginData = {
      username_or_email: data.username,
      password: data.password,
      remember_me: rememberMe,
    };

    try {
      await new Promise((resolve, reject) => {
        login(loginData, {
          onSuccess: (responseData) => {
            if (responseData?.mfa_required) {
              // MFA required — switch to OTP step
              setCachedCredentials(loginData);
              setMfaSession(responseData.mfa_session);
              resolve();
            } else {
              // MFA disabled — login complete
              resolve();
              navigate(getPostLoginDestination(responseData?.user, from), { replace: true });
            }
          },
          onError: reject,
        });
      });
    } catch (error) {
      console.error('Login submission failed:', error);
    }
  };

  // Step 2 — OTP submit
  const onOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpError('');

    if (!otp || otp.length !== 6) {
      setOtpError(t('mfa.errors.invalidCode'));
      return;
    }

    setOtpLoading(true);
    try {
      const data = await api.auth.verifyMfa({ mfa_session: mfaSession, otp, remember_me: rememberMe });
      if (data.token) {
        localStorage.setItem('authToken', data.token);
      }
      const destination = getPostLoginDestination(data?.user, from);
      navigate(destination, { replace: true });
      // Force page reload to re-initialise auth context with the new token
      window.location.href = destination;
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('mfa.errors.failed');
      setOtpError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend — re-run login with cached credentials to get a fresh OTP
  const onResend = async () => {
    if (!cachedCredentials || resendLoading) return;
    setResendLoading(true);
    setOtpError('');
    setOtp('');
    try {
      await new Promise((resolve, reject) => {
        login(cachedCredentials, {
          onSuccess: (responseData) => {
            if (responseData?.mfa_required) {
              setMfaSession(responseData.mfa_session);
            }
            resolve();
          },
          onError: reject,
        });
      });
    } catch (_) {
      setOtpError(t('mfa.errors.resendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  // ── OTP STEP ──────────────────────────────────────────────────────────────
  if (mfaSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
        <LurganLogo className="h-14 mb-6 [&_path]:fill-black dark:[&_path]:fill-white" aria-label="Coláiste Lurgan" />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSwitcher />
            </div>
            <CardTitle className="text-2xl font-bold">
              {t('mfa.title')}
            </CardTitle>
            <CardDescription>
              {t('mfa.instruction')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onOtpSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">{t('mfa.codeLabel')}</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className={`text-center text-xl tracking-widest ${otpError ? 'border-destructive' : ''}`}
                  disabled={otpLoading}
                  autoFocus
                />
                {otpError && (
                  <p className="text-sm text-destructive">{otpError}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={otpLoading}>
                {otpLoading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" className="border-primary-foreground" />
                    <span>{t('mfa.verifying')}</span>
                  </div>
                ) : (
                  t('mfa.verify')
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resendLoading}
                  className="text-sm text-muted-foreground hover:text-foreground underline disabled:opacity-50"
                >
                  {resendLoading ? t('mfa.resending') : t('mfa.resend')}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── LOGIN STEP ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
      <LurganLogo className="h-14 mb-6 [&_path]:fill-black dark:[&_path]:fill-white" aria-label="Coláiste Lurgan" />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <BainisteoirLogo className="h-10 mx-auto text-black dark:text-white" aria-label="Bainisteoir" />
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Username Field */}
            <div className="space-y-2">
              <Label htmlFor="username">
                {t('login.username')}
              </Label>
              <Input
                id="username"
                type="text"
                placeholder={t('login.username').toLowerCase()}
                {...register('username')}
                className={errors.username ? 'border-destructive' : ''}
                disabled={isLoggingIn}
              />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password">
                {t('login.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.password').toLowerCase()}
                  {...register('password')}
                  className={errors.password ? 'border-destructive' : ''}
                  disabled={isLoggingIn}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  disabled={isLoggingIn}
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center gap-2">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
                disabled={isLoggingIn}
              />
              <Label htmlFor="rememberMe" className="font-normal cursor-pointer">
                {t('login.rememberMe')}
              </Label>
            </div>

            {/* Login Error */}
            {loginError && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                <p>
                  {loginError.response?.data?.detail ||
                   loginError.response?.data?.non_field_errors?.[0] ||
                   t('login.errors.unauthorized')}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={isLoggingIn}>
              {isLoggingIn ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" className="border-primary-foreground" />
                  <span>{t('login.loggingIn')}</span>
                </div>
              ) : (
                t('login.submit')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;
